import type { ProductoAtributoRow } from '../types/productoAtributos';

function normalizeTipo(raw: unknown): ProductoAtributoRow['tipo'] {
  const t = String(raw ?? 'texto').toLowerCase().trim();
  if (t === 'numero' || t === 'número') return 'numero';
  return 'texto';
}

function newRowId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `atr-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/** Convierte filas del formulario al JSON guardado en productos.atributos (solo claves persistidas). */
export function atributosRowsToDbPayload(rows: ProductoAtributoRow[]): Record<string, unknown>[] {
  return rows
    .filter((r) => {
      const n = (r.nombre || '').trim();
      if (!n) return false;
      return String(r.valor ?? '').trim() !== '';
    })
    .map((r) => {
      const tipo: ProductoAtributoRow['tipo'] = r.tipo === 'numero' ? 'numero' : 'texto';
      const base: Record<string, unknown> = {
        nombre: r.nombre.trim(),
        tipo,
        valor: String(r.valor ?? '').trim(),
      };
      if (tipo === 'numero' && (r.unidad || '').trim()) base.unidad = r.unidad!.trim();
      return base;
    });
}

/** Carga desde DB: array tipado u objeto legacy (listados). Tipos legacy (booleano, selección) se leen como texto. */
export function normalizeAtributosFromDb(raw: unknown): ProductoAtributoRow[] {
  if (raw == null) return [];
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (!t) return [];
    try {
      return normalizeAtributosFromDb(JSON.parse(t) as unknown);
    } catch {
      return [];
    }
  }
  if (Array.isArray(raw)) {
    const out: ProductoAtributoRow[] = [];
    for (const item of raw) {
      if (!item || typeof item !== 'object') continue;
      const o = item as Record<string, unknown>;
      const nombre = String(o.nombre ?? '').trim();
      if (!nombre) continue;
      const rawTipo = String(o.tipo ?? 'texto').toLowerCase().trim();
      const legacyBooleano = rawTipo === 'booleano';
      const tipo = normalizeTipo(o.tipo);
      let valor = String(o.valor ?? '');
      if (legacyBooleano) {
        const b =
          o.valor === true ||
          o.valor === 'true' ||
          valor.toLowerCase() === 'sí' ||
          valor.toLowerCase() === 'si' ||
          valor === '1';
        valor = b ? 'Sí' : 'No';
      }
      const row: ProductoAtributoRow = {
        id: newRowId(),
        nombre,
        valor,
        tipo,
      };
      if (o.unidad != null && String(o.unidad).trim()) row.unidad = String(o.unidad);
      out.push(row);
    }
    return out;
  }
  if (typeof raw === 'object') {
    return Object.entries(raw as Record<string, unknown>)
      .filter(([k, v]) => k && v != null && String(v).trim() !== '')
      .map(([k, v]) => ({
        id: newRowId(),
        nombre: k,
        valor: String(v),
        tipo: 'texto' as const,
      }));
  }
  return [];
}

/** Payload para RPC upsert_atributos_catalogo (sin valor). */
export function atributosRowsToCatalogRpcJson(rows: ProductoAtributoRow[]): Record<string, unknown>[] {
  return rows
    .filter((r) => (r.nombre || '').trim())
    .map((r) => {
      const tipo: ProductoAtributoRow['tipo'] = r.tipo === 'numero' ? 'numero' : 'texto';
      const o: Record<string, unknown> = {
        nombre: r.nombre.trim(),
        tipo,
      };
      if ((r.unidad || '').trim()) o.unidad = r.unidad!.trim();
      return o;
    });
}

export function formatAtributoValorDisplay(_tipo: ProductoAtributoRow['tipo'], valor: string): string {
  return valor || '';
}

/** Líneas "Nombre: Valor [unidad]" para catálogo público y listas. */
export function formatAtributosDisplayLines(
  raw: unknown
): { label: string; value: string; unidad?: string }[] {
  const rows = normalizeAtributosFromDb(raw);
  const out: { label: string; value: string; unidad?: string }[] = [];
  for (const r of rows) {
    const vDisp = formatAtributoValorDisplay(r.tipo, r.valor);
    if (!r.nombre.trim() || !vDisp.trim()) continue;
    out.push({
      label: r.nombre.trim(),
      value: vDisp,
      unidad: r.tipo === 'numero' && (r.unidad || '').trim() ? r.unidad!.trim() : undefined,
    });
  }
  return out;
}

export const LISTADO_ATRIBUTO_PRESETS: Record<
  string,
  { nombre: string; valor: string; tipo: ProductoAtributoRow['tipo'] }[]
> = {
  Inmueble: [
    { nombre: 'Habitaciones', valor: '', tipo: 'texto' },
    { nombre: 'Baños', valor: '', tipo: 'texto' },
    { nombre: 'Estrato', valor: '', tipo: 'texto' },
    { nombre: 'Parqueadero', valor: 'Sí', tipo: 'texto' },
    { nombre: 'Estado', valor: 'Usado', tipo: 'texto' },
  ],
  Vehículo: [
    { nombre: 'Marca', valor: '', tipo: 'texto' },
    { nombre: 'Modelo', valor: '', tipo: 'texto' },
    { nombre: 'Año', valor: '', tipo: 'texto' },
    { nombre: 'Kilometraje', valor: '', tipo: 'texto' },
    { nombre: 'Color', valor: '', tipo: 'texto' },
    { nombre: 'Transmisión', valor: 'Manual', tipo: 'texto' },
  ],
  Empleo: [
    { nombre: 'Cargo', valor: '', tipo: 'texto' },
    { nombre: 'Modalidad', valor: 'Presencial', tipo: 'texto' },
    { nombre: 'Salario', valor: '', tipo: 'texto' },
    { nombre: 'Horario', valor: '', tipo: 'texto' },
  ],
};
